import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {AbstractO2dElement} from "@o2d/shared";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {styleMap} from "lit/directives/style-map.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {createRef, ref} from "lit/directives/ref.js";
import {translate} from "lit-translate";
import {container} from "tsyringe";

import {RxjsLifecycleController} from "../_reactive-controllers/rxjs-lifecycle.controller";
import {JustMovedFlashController} from "../_reactive-controllers/just-moved-flash.controller";
import {DissolveFlashController} from "../_reactive-controllers/dissolve-flash.controller";
import {EntityLiveRefreshController} from "../_reactive-controllers/entity-live-refresh.controller";
import {IssuePersonalDataRepository} from "../../domain/repositories/issue-personal-data.repository";
import {diffDoneRows} from "../../domain/helpers/diff-done-rows.helper";
import {CommandBus} from "../../services/command-bus.service";
import {AssignIssueToPersonalLabelCommand} from "../../commands/assign-issue-to-personal-label.command";
import {MoveIssueToAnotherPersonalLabelCommand} from "../../commands/move-issue-to-another-personal-label.command";
import {ReorderIssueOfPersonalLabelCommand} from "../../commands/reorder-issue-of-personal-label.command";
import {UpdatePersonalLabelCommand} from "../../commands/update-personal-label.command";
import {UnassignIssueFromPersonalLabelCommand} from "../../commands/unassign-issue-from-personal-label.command";
import {DeletePersonalLabelCommand} from "../../commands/delete-personal-label.command";
import {BulkCommand} from "../../commands/bulk.command";
import type {ICommand} from "../../commands/i-command.interface";
import {IssueHelper} from "../../domain/helpers/issue.helper";
import {UiHelper} from "../../helpers/ui.helper";
import type {PersonalLabel} from "../../domain/entities/personal-label.entity";
import {Issue} from "../../domain/entities/issue.entity";
import type {IssuePersonalData} from "../../domain/entities/issue-personal-data.entity";
import {
    MY_ORGANIZER_ISSUE_LABELED_EVENT,
    MY_ORGANIZER_ISSUE_UNLABELED_EVENT,
    MY_ORGANIZER_LABEL_DELETED_EVENT,
    MyOrganizerDragContext,
    type MyOrganizerIssueLabeledEventDetail,
    type MyOrganizerIssueUnlabeledEventDetail,
    type MyOrganizerLabelDeletedEventDetail,
} from "./my-organizer-drag-context";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";
import {itemColorStyle} from "../../helpers/header-item-style.helper";

const DATE_FORMAT = {day: `2-digit`, month: `2-digit`, year: `numeric`} as const;

/** A row in this column, paired with its resolved Issue for rendering. */
interface LabelColumnRow {
    issue: Issue;
    row: IssuePersonalData;
}

/**
 * A drop-sensitive position between two rows (or before the first / after the
 * last) — identified by the uid of the row that would end up immediately
 * AFTER the dropped Issue, or `null` for "at the very end" (Anke, 2026-09-06:
 * "ich möchte nicht die obere und untere Kante der vorhandenen issues in der
 * Spalte verwenden, sondern zwischen den issues kleine schmale Rechtecke, die
 * komplett sensitiv sind" — replaces the previous top-half/bottom-half-of-a-
 * row scheme, where hovering the bottom edge of row A and the top edge of row
 * B both targeted the exact same gap via two different, easily confused hit
 * zones: "ich möchte vermeiden, dass es zwei Linien gibt, wo ich das issue
 * fallenlassen kann für die gleiche Endposition"). Each gap between rows now
 * has exactly ONE dedicated, fully-sensitive rectangle (see `_renderGap()`),
 * so there is exactly one hit zone per possible end position.
 */
interface DropIndicator {
    insertBeforeRowUid: string | null;
}

/** One item in the interleaved gap/row list this column renders — see `render()`'s own doc comment. */
type RenderItem =
    | { kind: 'row'; pair: LabelColumnRow }
    | { kind: 'gap'; insertBeforeRowUid: string | null };

/**
 * "My Organizer" mode's personal-label column (Anke, 2026-09-06, Schritt 1):
 * one column per personal label ("Meine Box" by default — see
 * `o2d-personal-organizer.view.ts`'s ensure-default-label step), showing the
 * Issues currently assigned to THIS label and accepting a drop from
 * `<my-organizer-assigned-column>` (or another label column) to assign/move
 * one, plus manual reordering within the column ("My Organizer" reorder step,
 * 2026-09-06).
 *
 * This component itself doesn't care how many instances of it exist, that's
 * the view's concern. **Multiple columns at once** (Anke, 2026-09-06: "Neue Box anlegen" on the
 * "Meine Boxen" page) IS now supported end-to-end — see `o2d-personal-
 * organizer.view.ts`'s own doc comment, and the cross-column live-sync
 * listener in `connectedCallback()` below (an Issue Moved OUT of this
 * column, by a drop onto a DIFFERENT `<my-organizer-label-column>` instance,
 * needs to disappear from here too). **Renaming** (Anke, same day: "im
 * nächsten Schritt machen wir die Namen editierbar... das kannst du gern
 * schon mit machen") is ALSO now supported — click the header text to turn
 * it into an input, Enter/blur commits (via `UpdatePersonalLabelCommand`),
 * Escape cancels — see `_onHeaderClick()`/`_saveLabelRename()` below.
 *
 * **Deleting a box** (Anke, 2026-09-09: "füge einen Button hinzu, um eine
 * Box zu löschen. nach Klick bitte den Standard-Bestätigungsdialog
 * einblenden. dann löschen. die Tickets, die enthalten waren in der Box,
 * wandern wieder zurück in die Inbox.") — the header's delete button opens
 * `UiHelper.confirm()` (the same `<ha-confirm>` dialog/wording convention as
 * `dashboard-creator.component.ts`'s own tile delete), then, on
 * confirmation, ONE `BulkCommand`: every currently-shown row's
 * `personalLabel` cleared via `UnassignIssueFromPersonalLabelCommand` first,
 * then `DeletePersonalLabelCommand` last. NOT actually atomic (review-fund,
 * 2026-09-09 — corrected from an earlier version of this comment that
 * claimed it was: `BulkCommandHandler` dispatches each sub-command in a
 * plain loop, no surrounding DB transaction; a partial failure has no
 * rollback), a pre-existing property of `BulkCommand` this call site just
 * inherits, same as every other use of it in this codebase. Unassigning
 * first is required, not cosmetic — the backend only SOFT-deletes a
 * `PersonalLabel` and does nothing to the rows that still reference it
 * (verified against `IssuePersonalData::getPersonalLabelSafe()`'s own
 * "likely soft-deleted label" log line) — without explicitly clearing
 * `personalLabel` on each row first, every ticket that was in this box
 * would stay orphaned, referencing a label that no longer resolves,
 * instead of "wandering back
 * to the Inbox". On success, this dispatches `MY_ORGANIZER_ISSUE_UNLABELED_EVENT`
 * once per returned ticket (`<my-organizer-assigned-column>` — the Inbox —
 * listens for exactly this, see its own doc comment) and its OWN
 * `MY_ORGANIZER_LABEL_DELETED_EVENT` so the owning view removes this column.
 *
 * Extends `AbstractO2dElement` (`@o2d/shared`), not the legacy
 * `AbstractLitElement` — mandatory for new components per ADR-007. RxJS
 * lifecycle (auto-unsubscribe on disconnect) comes via the `RxjsLifecycleController`
 * Reactive Controller (Composition), not base-class inheritance — see
 * ADR-007 §2/§6.
 */
@customElement('my-organizer-label-column')
export class MyOrganizerLabelColumnComponent extends AbstractO2dElement {

    @property({attribute: false})
    public label!: PersonalLabel;

    @state()
    private _rows: LabelColumnRow[] = [];

    @state()
    private _loaded = false;

    @state()
    private _isDropTarget = false;

    @state()
    private _dropIndicator: DropIndicator | null = null;

    @state()
    private _draggingIssueUid: string | null = null;

    /**
     * Guards against a double-click on the delete button (review-fund,
     * 2026-09-09): without this, a second click before the first
     * `dispatchCommand()` returns would fire a second, overlapping
     * `BulkCommand` against uids the first one may already have
     * invalidated server-side. Set at the very START of `_onDeleteClick()`
     * — BEFORE the confirm dialog, not after (round 2: an earlier version
     * of this flag only started guarding after the dialog resolved, which
     * left the dialog's own open window unguarded against a fast enough
     * double-click).
     */
    @state()
    private _isDeleting = false;

    /**
     * "Pressed" (Anke, 2026-09-06: "bitte die Zustände hover und gedrückt
     * trennen... für gedrückt #ff0000") — tracked in JS by `issue.uid`,
     * deliberately NOT via the native `:active` CSS pseudo-class. Bug report
     * (2026-09-06, same day): with `:active`, dropping an Issue WITHOUT
     * moving the mouse afterwards left a DIFFERENT, uninvolved row colored
     * red. Root cause: `:active`/`:hover` are matched by the browser against
     * WHATEVER element is currently under the pointer's last known screen
     * position — not by data identity. The drop reorders `_rows`, rows
     * upstream of the dragged one's old slot shift up, and if the pointer
     * never moves afterward, the row that slides into that exact screen
     * rectangle inherits `:active` purely because it now occupies that
     * pixel — regardless of which Issue it renders. `repeat()`'s keying
     * (see `render()`'s own doc comment) fixed the equivalent problem for
     * OUR OWN classes (`--dragging`/`--just-moved`), but cannot fix a
     * browser-native pseudo-class re-evaluated against post-reflow layout.
     * Tracking "pressed" the same way as `_draggingIssueUid` (by uid, set on
     * `mousedown`, cleared on any `mouseup` anywhere via a window listener —
     * not the row's own `mouseup`, which would be exactly as reflow-fragile
     * as `:active` itself) sidesteps the browser's hit-testing entirely.
     */
    @state()
    private _pressedIssueUid: string | null = null;

    /**
     * "Just moved here" flash (Anke, 2026-09-06: "damit man noch sehen kann,
     * welches issue ich gerade verschoben habe") — after ANY successful drop
     * that lands an Issue in this column (Assign/Move/Reorder), its row
     * briefly shows the hover highlight color, then fades back to normal
     * over 2s. See `JustMovedFlashController`'s own doc comment for the full
     * two-phase reasoning — shared with `<my-organizer-assigned-column>`/
     * `<my-organizer-calendar-day>` instead of a private copy (review-fund,
     * 2026-09-07).
     */
    private readonly _justMoved = new JustMovedFlashController(this);

    /**
     * Inline rename (Anke, 2026-09-06: "im nächsten Schritt machen wir die
     * Namen editierbar"). `_labelDraft` is a separate draft string, not a
     * direct binding to `this.label.label` — editing must not mutate the
     * live, change-tracked `PersonalLabel` proxy until the User actually
     * commits (Enter/blur), so Escape/an unfocused cancel has something to
     * simply discard instead of having already corrupted the tracked entity.
     */
    @state()
    private _editingLabel = false;

    @state()
    private _labelDraft = '';

    private readonly _labelInputRef = createRef<HTMLInputElement>();

    public readonly rxjs = new RxjsLifecycleController(this);

    /** "Went DONE" dissolve animation (Anke, 2026-09-09) — see
     *  `DissolveFlashController`'s own doc comment and `_liveRefresh` below. */
    private readonly _dissolve = new DissolveFlashController(this);

    /**
     * Live-refresh (Anke, 2026-09-09 bug report: "wenn ich aus der My
     * Organizer-Ansicht auf ein Ticket klicke... den Status ändere und
     * speichere, dann ist diese Änderung nicht sofort in der My Organizer-
     * Ansicht zu sehen, sondern erst nach Neuladen. bei der Timeline ist
     * das sofort zu sehen.") — same `EntityLiveRefreshController` the
     * Timeline views and `<ha-combobox>` already use for exactly this
     * (`PRD-061`/`PRD-043`).
     *
     * NOT a plain `_load()` (review-fund, 2026-09-09, round 2, follow-up:
     * "wenn ein Ticket... auf done gesetzt wird... möchte ich, dass man das
     * Ticket noch kurz sieht und es dann mit einer Auflöseanimation...
     * verschwindet") — a status edit that moves an Issue to DONE must NOT
     * just vanish from `_rows` the instant this reload runs; `diffDoneRows()`
     * (`domain/helpers/diff-done-rows.helper.ts`) tells us WHICH rows just
     * went DONE, kept in `_rows` for now, and `_dissolve.start()` animates
     * each one away, only THEN removing it. Every other kind of change
     * (title, status to something other than DONE, etc.) still updates
     * `_rows` immediately, same as before.
     */
    private readonly _liveRefresh = new EntityLiveRefreshController(this, {
        getEntityClass: () => Issue,
        onReload: () => this._reloadLive(),
    });

    /** Bumped once per `_reloadLive()` call — see that method's own
     *  generation check (review-fund, 2026-09-09, round 3). */
    private _reloadGeneration = 0;

    private async _reloadLive(): Promise<void> {
        // Generation guard (review-fund, 2026-09-09, round 3 — the SAME
        // race this pattern was already fixed for in `<my-organizer-
        // assigned-column>`, round 2, just missing here until now):
        // `EntityLiveRefreshController` calls `onReload()` per matching
        // event with no serialization of its own — two `entity.changed`
        // events close together can start two overlapping `_reloadLive()`
        // calls. If the OLDER one's `_fetchRawRows()` resolves AFTER a
        // NEWER one already landed, it must not overwrite `_rows` with its
        // now-stale result.
        const generation = ++this._reloadGeneration;
        const freshRawRows = await this._fetchRawRows();
        if (generation !== this._reloadGeneration) {
            return; // superseded while this fetch was in flight.
        }

        // `uid => this._dissolve.has(uid)` (review-fund, 2026-09-09, round
        // 4): without it, a row already mid-dissolve from a PRIOR reload
        // would be reported as `newlyDone` again on every subsequent one
        // (it's still DONE, by definition, until actually removed) —
        // `DissolveFlashController.start()` restarts its ENTIRE timeline
        // rather than leaving an in-flight one alone, visibly snapping the
        // row back and re-pausing on every overlapping live-refresh.
        const {rows, newlyDone} = diffDoneRows(this._rows, freshRawRows, uid => this._dissolve.has(uid));
        this._rows = rows;
        this._loaded = true;

        for (const pair of newlyDone) {
            this._dissolve.start(pair.issue.uid, () => {
                this._rows = this._rows.filter(r => r.row.uid !== pair.row.uid);
            });
        }
    }

    public override connectedCallback(): void {
        super.connectedCallback();

        // Cross-column live sync — unsubscribes automatically on disconnect
        // via the RxjsLifecycleController, no manual `disconnectedCallback`
        // override/listener bookkeeping needed.
        this.rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueUnlabeledEventDetail>>(window, MY_ORGANIZER_ISSUE_UNLABELED_EVENT)
            .subscribe(event => {
                const {issueUid} = event.detail;
                this._rows = this._rows.filter(r => r.issue.uid !== issueUid);
            });

        // The reverse direction of the same rule (Anke, 2026-09-06: "Neue Box
        // anlegen" — multiple `<my-organizer-label-column>` instances can now
        // be on screen at once): if some OTHER column just Assigned/Moved an
        // Issue to ITS label, and that Issue happens to be in OUR OWN
        // `_rows` (Move away from us), drop it here too — an Issue belongs to
        // exactly one label column. `personalLabelUid` distinguishes "this
        // event is about MY OWN label" (already applied locally by the
        // dispatching column's own success branch, see `_handleDrop` below —
        // reacting to it again here would incorrectly remove the row we just
        // added) from "a DIFFERENT column's label" (see the event's own doc
        // comment on `MyOrganizerIssueLabeledEventDetail`).
        this.rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueLabeledEventDetail>>(window, MY_ORGANIZER_ISSUE_LABELED_EVENT)
            .subscribe(event => {
                const {issueUid, personalLabelUid} = event.detail;
                if (personalLabelUid === this.label.uid) {
                    return;
                }
                this._rows = this._rows.filter(r => r.issue.uid !== issueUid);
            });

        // Clears "pressed" wherever the mouse button comes up — NOT the
        // row's own mouseup, which would be exactly as fragile as native
        // `:active` (see `_pressedIssueUid`'s own doc comment).
        this.rxjs.listenUntilDisconnected<MouseEvent>(window, 'mouseup')
            .subscribe(() => {
                this._pressedIssueUid = null;
            });

        this._load();
    }

    private _onRowMouseDown(issueUid: string): void {
        this._pressedIssueUid = issueUid;
    }

    /** Focuses/selects the rename input the moment it appears, so the User can start typing immediately. */
    public override updated(changedProperties: PropertyValues): void {
        if (changedProperties.has('_editingLabel') && this._editingLabel) {
            this._labelInputRef.value?.focus();
            this._labelInputRef.value?.select();
        }
    }

    private _onHeaderClick(): void {
        this._labelDraft = this.label.label;
        this._editingLabel = true;
    }

    private _onLabelDraftInput(event: Event): void {
        this._labelDraft = (event.target as HTMLInputElement).value;
    }

    private _onLabelDraftKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            (event.target as HTMLInputElement).blur(); // commits via _onLabelDraftBlur — single source of truth for "save".
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this._editingLabel = false; // discard the draft, no dispatch.
        }
    }

    /**
     * Checks `_editingLabel` first — Escape already flips it to `false`
     * SYNCHRONOUSLY before the input's native `blur` fires (it's still the
     * focused element at that point), so this guards against saving a draft
     * the User just explicitly cancelled.
     */
    private _onLabelDraftBlur(): void {
        if (!this._editingLabel) {
            return;
        }
        this._saveLabelRename();
    }

    /**
     * Optimistic, mirroring the drop handlers below: applies the new text to
     * the live `PersonalLabel` proxy immediately (so this render already
     * shows it), dispatches, and reverts on failure. A blank or unchanged
     * draft is silently discarded — not an error, just nothing to persist.
     */
    private async _saveLabelRename(): Promise<void> {
        this._editingLabel = false;
        const trimmed = this._labelDraft.trim();
        if (trimmed === '' || trimmed === this.label.label) {
            return;
        }

        const previous = this.label.label;
        this.label.label = trimmed;

        const dispatched = await container.resolve(CommandBus).dispatchCommand(
            new UpdatePersonalLabelCommand('personal-label', this.label.uid, this.label)
        );

        if (!dispatched) {
            console.error('[my-organizer-label-column] failed to rename this personal label');
            this.label.label = previous;
            this.requestUpdate();
        }
    }

    /**
     * The delete button — see the class doc comment for the full flow and
     * why unassigning every row is required, not cosmetic.
     *
     * `pairs` is deliberately read TWICE (review-fund, 2026-09-09): once
     * just to build the confirm dialog's own text (how many tickets are
     * affected), and AGAIN from `_rows` right after the user confirms —
     * NOT reused from before the `await UiHelper.confirm()`. That `await`
     * waits on a modal, user-paced dialog; meanwhile `_rows` can change
     * from something entirely outside this click (a live-refresh reload,
     * or a card dropped into this very box from elsewhere). Building the
     * `BulkCommand` from a stale, pre-confirm snapshot would either miss
     * unassigning a row that arrived during the dialog (leaving it
     * orphaned, referencing the label this call is about to delete) or
     * include one that's already gone. `_rows` itself is never MUTATED
     * here, only read — this component is about to be removed by the
     * parent view entirely, per `MY_ORGANIZER_LABEL_DELETED_EVENT` below —
     * so there's no "revert the LIST" case to support here, unlike every
     * other optimistic mutation in this file. There IS still a revert of
     * the individual `personalLabel` fields below on a failed dispatch,
     * exactly like those other mutations.
     */
    private async _onDeleteClick(): Promise<void> {
        if (this._isDeleting) {
            return; // already in flight — see `_isDeleting`'s own doc comment.
        }

        // Set BEFORE the confirm dialog, not after (review-fund, 2026-09-09,
        // round 2 — the first version of this guard only set it after
        // `await confirmed`, leaving the dialog itself unguarded: a click
        // handler runs synchronously only up to its first `await`, so a
        // second, fast click on the still-not-yet-`disabled` button could
        // reach this method again before the FIRST dialog even resolved,
        // opening a second, overlapping confirm — and if both got confirmed,
        // two overlapping `BulkCommand`s for the same box). Reset back to
        // `false` below if the user declines, so a later, genuine delete
        // attempt isn't permanently locked out.
        this._isDeleting = true;

        const confirmed = await UiHelper.confirm({
            title: 'Wirklich löschen?',
            content: html`<p>Die Box "${this.label.label}" wird gelöscht.${this._rows.length > 0
                ? ' Die enthaltenen Tickets wandern zurück in die Inbox.'
                : ''}</p>`,
            confirmLabel: 'Ja, jetzt löschen!',
            cancelLabel: 'Nein, abbrechen!',
        });
        if (!confirmed) {
            this._isDeleting = false;
            return;
        }

        const pairs = this._rows; // re-read AFTER the confirm — see the method's own doc comment.

        // Mutated up front, together with everything else in the SAME bulk
        // — mirrors `_dispatchWithRenumbering()`'s own "mutate before the
        // dispatch whose payload IS this row" ordering below.
        pairs.forEach(({row}) => {
            row.personalLabel = null;
        });

        // NOT actually atomic (review-fund, 2026-09-09, verified against
        // `BulkCommandHandler`: it dispatches each sub-command in a plain
        // loop, no surrounding DB transaction) — a partial failure (e.g.
        // an Unassign succeeds, the Delete that follows it does not) can
        // leave an inconsistent state with no rollback. Accepted for now,
        // consistent with every other `BulkCommand` use in this codebase;
        // not something this one call site can fix on its own.
        const commands: ICommand[] = [
            ...pairs.map(({row}) => new UnassignIssueFromPersonalLabelCommand('issue-personal-data', row.uid, row)),
            new DeletePersonalLabelCommand('personal-label', this.label.uid, this.label),
        ];

        const dispatched = await container.resolve(CommandBus).dispatchCommand(new BulkCommand(commands));
        if (!dispatched) {
            console.error('[my-organizer-label-column] failed to delete this personal label');
            pairs.forEach(({row}) => {
                row.personalLabel = Object.assign(Promise.resolve(this.label), {uid: this.label.uid});
            });
            this._isDeleting = false;
            return;
        }

        for (const {issue} of pairs) {
            window.dispatchEvent(new CustomEvent<MyOrganizerIssueUnlabeledEventDetail>(
                MY_ORGANIZER_ISSUE_UNLABELED_EVENT, {detail: {issueUid: issue.uid}}
            ));
        }
        // Reset explicitly here too (review-fund, 2026-09-09, round 2) even
        // though this component is about to be removed by the parent view
        // (`MY_ORGANIZER_LABEL_DELETED_EVENT` below) — an explicit guarantee
        // instead of relying on that removal alone, in case a future
        // refactor of the parent view ever delays or drops it.
        this._isDeleting = false;
        this.dispatchEvent(new CustomEvent<MyOrganizerLabelDeletedEventDetail>(
            MY_ORGANIZER_LABEL_DELETED_EVENT, {detail: {labelUid: this.label.uid}}
        ));
    }

    /**
     * Appends a row to `_rows` and immediately re-sorts by `sortOrder`
     * (Anke, 2026-09-06 bug report: "es soll an der Stelle bleiben, an der
     * ich es fallenlasse... im Moment landet es immer am Ende") — a plain
     * `[...this._rows, pair]` DOES persist the correct, positionally-computed
     * `sortOrder` to the backend, but always renders the new row at the END
     * of the local array regardless of that value, until the next full
     * `_load()`. Mirrors `_reorderWithinColumn()`'s own re-sort after
     * mutating `sortOrder`.
     */
    private _addRowSorted(pair: LabelColumnRow): void {
        this._rows = [...this._rows, pair].sort((a, b) => a.row.sortOrder - b.row.sortOrder);
    }

    /**
     * The value the BACKEND will assign to a row appended via a background
     * (non-positional) drop — mirrors `IssuePersonalDataRepository::
     * getMaxSortOrder()+100` client-side. A background drop deliberately
     * sends no `sortOrder` in its payload (the backend computes its own), but
     * the row's LOCAL `sortOrder` must still be corrected to this same value
     * before `_addRowSorted()` — otherwise it keeps whatever stale value it
     * had (a different column's position, or the `0` default for a brand-new
     * row), which would sort it to the wrong spot locally until the next
     * full `_load()`.
     */
    private _computeAppendSortOrder(): number {
        return this._rows.reduce((max, r) => Math.max(max, r.row.sortOrder), 0) + 100;
    }

    /**
     * Every row for this label, resolved with its Issue — INCLUDING a DONE
     * one. Split out from `_load()` so `_reloadLive()` above can diff
     * against this same raw fetch (Anke, 2026-09-09) instead of duplicating
     * the fetch+resolve logic.
     */
    private async _fetchRawRows(): Promise<LabelColumnRow[]> {
        const rows = await container.resolve(IssuePersonalDataRepository).findAllByPersonalLabel(this.label.uid);
        const pairs = await Promise.all(rows.map(async row => ({issue: await row.issue, row})));
        return pairs.filter((pair): pair is LabelColumnRow => !!pair.issue);
    }

    /**
     * The initial load (and any other full reload that isn't the live-
     * refresh path above) — a DONE Issue is excluded outright here, never
     * even shown for a moment: only a LIVE transition into DONE (caught by
     * `_reloadLive()`) gets the dissolve animation (Anke, 2026-09-09: "es
     * gehört ja dann im Zustand done auch nicht mehr in die Ansicht rein").
     */
    private async _load(): Promise<void> {
        const rows = await this._fetchRawRows();
        this._rows = rows.filter(pair => !IssueHelper.isDone(pair.issue));
        this._loaded = true;
    }

    /**
     * `repeat()` with a stable key per item — `row.uid` for a row, a
     * `gap:<uid|end>` key for a gap (Anke bug report, 2026-09-06: "das
     * Element, was jetzt auf Platz 1 steht ist weiterhin #ff0000 gefärbt,
     * obwohl es am Prozess gar nicht beteiligt war") — NOT a plain
     * `_rows.map(...)`. This column reorders `_rows` in place (reorder/
     * Assign/Move all change array order without any Issue being added or
     * removed), and a plain `.map()` inside a Lit template has no concept of
     * item identity: it reuses the Nth DOM node positionally for whatever
     * data is now the Nth array entry, regardless of whether that's the same
     * logical row as before. Native, DOM-node-scoped state — `:active`,
     * an in-flight `--just-moved-fading` CSS transition, drag state — then
     * stays attached to the (reused) physical element and visibly "jumps"
     * onto whatever Issue now occupies that position, instead of following
     * the Issue it actually belongs to. `repeat()` keys each rendered
     * template to its identity, so Lit moves/reuses DOM nodes BY THAT
     * IDENTITY — the physical element (and whatever native/CSS state clings
     * to it) travels WITH its own row/gap when the array reorders. A gap's
     * key is derived from the row that follows it (`gap:<that row's uid>`,
     * or `gap:end` for the trailing one after the last row) — that's stable
     * across a reorder too, since it names WHERE the gap sits relative to a
     * specific row, not a position index.
     */
    public render(): HTMLTemplateResult {
        return html`
            <div class="my-organizer-label-column ${this._isDropTarget ? 'my-organizer-label-column--drop-target' : ''}"
                 @dragover=${this._onDragOver}
                 @dragleave=${this._onDragLeave}
                 @drop=${this._onDrop}>
                <div class="my-organizer-label-column__header">
                    ${this._editingLabel
                        ? html`
                            <input class="my-organizer-label-column__header-input"
                                   ${ref(this._labelInputRef)}
                                   .value=${this._labelDraft}
                                   @input=${(event: Event) => this._onLabelDraftInput(event)}
                                   @keydown=${(event: KeyboardEvent) => this._onLabelDraftKeydown(event)}
                                   @blur=${() => this._onLabelDraftBlur()}
                            >
                        `
                        : html`
                            <span class="my-organizer-label-column__header-text"
                                  title=${translate('personal-organizer.label-column.rename-hint')}
                                  @click=${() => this._onHeaderClick()}
                            >${this.label.label}</span>
                        `}
                    <button type="button"
                            class="my-organizer-label-column__delete-button"
                            title=${translate('personal-organizer.label-column.delete-hint')}
                            ?disabled=${this._isDeleting}
                            @click=${() => this._onDeleteClick()}
                    ></button>
                </div>
                <div class="my-organizer-label-column__body">
                    ${this._loaded
                        ? repeat(
                            this._buildRenderItems(),
                            item => item.kind === 'row' ? item.pair.row.uid : `gap:${item.insertBeforeRowUid ?? 'end'}`,
                            item => item.kind === 'row' ? this._renderRow(item.pair) : this._renderGap(item.insertBeforeRowUid),
                        )
                        : html``}
                </div>
            </div>
        `;
    }

    /**
     * Interleaves a gap before the first row, one after every row, and one
     * after the last (`N` rows → `N+1` gaps) — see the `DropIndicator`/
     * `render()` doc comments for why gaps, not row-edge zones, are the drop
     * targets now.
     */
    private _buildRenderItems(): RenderItem[] {
        const items: RenderItem[] = [{kind: 'gap', insertBeforeRowUid: this._rows[0]?.row.uid ?? null}];
        this._rows.forEach((pair, index) => {
            items.push({kind: 'row', pair});
            const next = this._rows[index + 1];
            items.push({kind: 'gap', insertBeforeRowUid: next?.row.uid ?? null});
        });
        return items;
    }

    private _renderRow(pair: LabelColumnRow): HTMLTemplateResult {
        const {issue, row} = pair;

        return html`
            <div class=${classMap({
                'my-organizer-assigned-column__row': true,
                'my-organizer-assigned-column__row--pressed': this._pressedIssueUid === issue.uid,
                'my-organizer-assigned-column__row--dragging': this._draggingIssueUid === issue.uid,
                'my-organizer-assigned-column__row--just-moved': this._justMoved.isJustMoved(issue.uid),
                'my-organizer-assigned-column__row--just-moved-fading': this._justMoved.isFading(issue.uid),
                'my-organizer-assigned-column__row--dissolving': this._dissolve.isDissolving(issue.uid),
            })}
                 draggable="true"
                 @mousedown=${() => this._onRowMouseDown(issue.uid)}
                 @dragstart=${(event: DragEvent) => this._onDragStart(event, issue)}
                 @dragend=${this._onDragEnd}
                 @click=${() => this._onEditIssue(issue)}
                 data-root-type=${rootUidOf(issue.type) ?? nothing}>
                ${issue.status ? html`<div class="my-organizer-assigned-column__status"
                     style=${styleMap(itemColorStyle(issue.status.color, issue.status.textColor))}>${issue.status.key}</div>` : nothing}
                <div class="my-organizer-assigned-column__date">${issue.start.toLocaleString(DATE_FORMAT)}</div>
                <div class="my-organizer-assigned-column__title">${issue.title != '' ? issue.title : '-'}</div>
            </div>
        `;
    }

    /**
     * The sole row-level drop target now (Anke, 2026-09-06): a narrow, fully-
     * sensitive rectangle between rows (or before the first/after the last),
     * one per possible end position — see the `DropIndicator` doc comment for
     * why this replaces the old top-half/bottom-half-of-a-row scheme.
     */
    private _renderGap(insertBeforeRowUid: string | null): HTMLTemplateResult {
        const active = this._dropIndicator?.insertBeforeRowUid === insertBeforeRowUid;

        return html`
            <div class=${classMap({
                'my-organizer-label-column__gap': true,
                'my-organizer-label-column__gap--active': active,
            })}
                 @dragover=${(event: DragEvent) => this._onGapDragOver(event, insertBeforeRowUid)}
                 @dragleave=${this._onGapDragLeave}
                 @drop=${(event: DragEvent) => this._onGapDrop(event, insertBeforeRowUid)}>
            </div>
        `;
    }

    /**
     * Opens the standard Issue-edit dialog (Anke, 2026-09-06: "auf Klick auf
     * ein Issue den Standard Issue-Edit-Dialog öffnen") — see `<my-organizer-
     * assigned-column>`'s own doc comment on this same method for why a
     * plain `click` coexists fine with the row's `draggable`/`dragstart`.
     */
    private _onEditIssue(issue: Issue): void {
        void IssueHelper.openIssueEditDialog(issue);
    }

    /**
     * Same mechanism as `<my-organizer-assigned-column>`'s own `_onDragStart`
     * (see its doc comment on why both the shared drag-context AND
     * `dataTransfer.setData` are needed) — dragging a row back OUT of this
     * column (e.g. onto the assigned-issues column) uses the exact same
     * drag-context handoff, just in the opposite direction.
     */
    private _onDragStart(event: DragEvent, issue: Issue): void {
        MyOrganizerDragContext.draggedIssue = issue;
        this._pressedIssueUid = null; // --dragging (same color) takes over from here.
        this._draggingIssueUid = issue.uid;
        event.dataTransfer?.setData('text/plain', issue.uid);
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
        }
    }

    private _onDragEnd(): void {
        this._draggingIssueUid = null;
    }

    private _onDragOver(event: DragEvent): void {
        // Native HTML5 DnD refuses a drop on any element unless dragover is
        // explicitly prevented — without this, `drop` never fires at all.
        event.preventDefault();
        this._isDropTarget = true;
    }

    /**
     * `relatedTarget`-based, NOT "no gap called stopPropagation()" (review
     * finding): every gap-level `dragleave` calls `stopPropagation()`
     * unconditionally (to stop ITSELF from flickering the column's own
     * border), so that signal can never reach here to distinguish "still
     * inside the column" from "actually left it". `relatedTarget` — the
     * element the pointer moved TO — answers that directly regardless of
     * bubbling.
     */
    private _onDragLeave(event: DragEvent): void {
        const related = event.relatedTarget as (Node & EventTarget) | null;
        const column = event.currentTarget as { contains?(node: Node): boolean } | null;
        if (related && column?.contains?.(related)) {
            return; // moved onto a child (a row) — still inside the column.
        }
        this._isDropTarget = false;
        this._dropIndicator = null;
    }

    private _onGapDragOver(event: DragEvent, insertBeforeRowUid: string | null): void {
        event.preventDefault();
        event.stopPropagation();
        this._dropIndicator = {insertBeforeRowUid};
    }

    /**
     * Deliberately does NOT touch `_dropIndicator` — only `stopPropagation()`,
     * so the column-level handler never sees this event and never flickers
     * its own border. The next `dragover` (on whichever gap/row the pointer
     * lands on next) overwrites `_dropIndicator` anyway; leaving it stale for
     * the brief moment between two adjacent gaps is not visible in practice
     * and avoids a second, gap-level flicker.
     */
    private _onGapDragLeave(event: DragEvent): void {
        event.stopPropagation();
    }

    private async _onDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        this._isDropTarget = false;
        this._dropIndicator = null;

        const issue = MyOrganizerDragContext.draggedIssue;
        MyOrganizerDragContext.draggedIssue = null;
        if (!issue) {
            return;
        }

        await this._handleDrop(issue, null);
    }

    private async _onGapDrop(event: DragEvent, insertBeforeRowUid: string | null): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        this._isDropTarget = false;
        this._dropIndicator = null;

        const issue = MyOrganizerDragContext.draggedIssue;
        MyOrganizerDragContext.draggedIssue = null;
        if (!issue) {
            return;
        }

        await this._handleDrop(issue, {insertBeforeRowUid});
    }

    /**
     * The single entry point for both drop levels — decides Reorder vs.
     * Assign vs. Move (Review-Fund: previously ambiguous prose, now an
     * explicit, ordered decision tree; a self-drop can never be
     * misinterpreted as Move, because step 1 always wins for an Issue
     * already in this column's `_rows`).
     *
     * `target === null` means a background drop (not aimed at a specific
     * row) — the resulting row is appended at the end (no `sortOrder` sent,
     * the backend computes the append position itself).
     */
    private async _handleDrop(issue: Issue, target: DropIndicator | null): Promise<void> {
        // Step 1: already in THIS column (by local state, no network call) —
        // pure reorder, never Assign/Move.
        if (this._rows.some(r => r.issue.uid === issue.uid)) {
            if (!target) {
                return; // background drop of an already-shown Issue: no-op.
            }
            await this._reorderWithinColumn(issue, target);
            return;
        }

        const repository = container.resolve(IssuePersonalDataRepository);
        const existingRow = await repository.findOneByIssue(issue.uid);
        const labelUid = this.label.uid;
        const labelRef = Object.assign(Promise.resolve(this.label), {uid: labelUid});

        // Step 2a: the row already carries EXACTLY this label server-side,
        // but a fire-and-forget `_load()` race means it isn't in `_rows` yet
        // (Stefan-Todo -02). Treat as reorder/no-op, never Assign (which
        // would reject an already-labeled row) nor Move (nothing changes).
        if (existingRow?.personalLabel?.uid === labelUid) {
            this._addRowSorted({issue, row: existingRow});
            if (target) {
                await this._reorderWithinColumn(issue, target); // flashes on its own success.
            } else {
                this._justMoved.flash(issue.uid);
            }
            return;
        }

        // Step 2b: already assigned to a DIFFERENT label — Move.
        if (existingRow?.personalLabel) {
            existingRow.personalLabel = labelRef;
            const sortOrder = target ? this._computeSortOrder(target, issue.uid) : null;
            if (sortOrder === 'RENUMBER') {
                // 'move' — the renumbering bulk must carry the label change too,
                // otherwise the Issue only LOOKS moved (Stefan-Todo 0029-01).
                await this._dispatchWithRenumbering(issue, existingRow, target!, 'move');
                return;
            }
            if (sortOrder !== null) {
                existingRow.sortOrder = sortOrder;
            }

            const dispatched = await container.resolve(CommandBus).dispatchCommand(
                new MoveIssueToAnotherPersonalLabelCommand('issue-personal-data', existingRow.uid, existingRow)
            );
            if (dispatched) {
                if (sortOrder === null) {
                    existingRow.sortOrder = this._computeAppendSortOrder(); // local-only, mirrors the backend's own append computation.
                }
                this._addRowSorted({issue, row: existingRow});
                this._justMoved.flash(issue.uid);
                window.dispatchEvent(new CustomEvent<MyOrganizerIssueLabeledEventDetail>(
                    MY_ORGANIZER_ISSUE_LABELED_EVENT, {detail: {issueUid: issue.uid, personalLabelUid: labelUid}}
                ));
            } else {
                console.error('[my-organizer-label-column] failed to move the Issue to this label');
                await this._load();
            }
            return;
        }

        // Step 2c: no row yet, or a row with no label at all — Assign.
        const row = existingRow ?? await repository.create(issue);
        row.personalLabel = labelRef;
        const sortOrder = target ? this._computeSortOrder(target, issue.uid) : null;
        if (sortOrder === 'RENUMBER') {
            // 'assign' — and this is the case where omitting it also broke the
            // renumbering itself: `row` may be brand new from `repository.create()`,
            // so a Reorder against its unknown uid would fail server-side.
            await this._dispatchWithRenumbering(issue, row, target!, 'assign');
            return;
        }
        if (sortOrder !== null) {
            row.sortOrder = sortOrder;
        }

        const dispatched = await container.resolve(CommandBus).dispatchCommand(
            new AssignIssueToPersonalLabelCommand('issue-personal-data', row.uid, row)
        );
        if (dispatched) {
            if (sortOrder === null) {
                row.sortOrder = this._computeAppendSortOrder(); // local-only, mirrors the backend's own append computation.
            }
            this._addRowSorted({issue, row});
            this._justMoved.flash(issue.uid);
            window.dispatchEvent(new CustomEvent<MyOrganizerIssueLabeledEventDetail>(
                MY_ORGANIZER_ISSUE_LABELED_EVENT, {detail: {issueUid: issue.uid, personalLabelUid: labelUid}}
            ));
        } else {
            console.error('[my-organizer-label-column] failed to assign the Issue to this label');
            await this._load();
        }
    }

    /** Pure reordering within this column — never touches `personalLabel`. */
    private async _reorderWithinColumn(issue: Issue, target: DropIndicator): Promise<void> {
        const current = this._rows.find(r => r.issue.uid === issue.uid);
        if (!current) {
            return;
        }

        const sortOrder = this._computeSortOrder(target, issue.uid);
        if (sortOrder === 'RENUMBER') {
            await this._dispatchWithRenumbering(issue, current.row, target);
            return;
        }
        if (sortOrder === null || sortOrder === current.row.sortOrder) {
            return; // no target, or dropped back exactly where it already was — nothing to do.
        }

        current.row.sortOrder = sortOrder;
        this._rows = [...this._rows].sort((a, b) => a.row.sortOrder - b.row.sortOrder);

        const dispatched = await container.resolve(CommandBus).dispatchCommand(
            new ReorderIssueOfPersonalLabelCommand('issue-personal-data', current.row.uid, current.row)
        );
        if (dispatched) {
            this._justMoved.flash(issue.uid);
        } else {
            console.error('[my-organizer-label-column] failed to reorder the Issue within this column');
            await this._load();
        }
    }

    /**
     * Gap-based position (A/B → floor(mean); before the first → first-100;
     * after the last → last+100), excluding the dragged Issue itself from the
     * neighbor computation if it's already in this column. Returns
     * `'RENUMBER'` when the computed value would collide with the sentinel
     * (`<=0`) or with a neighbor (gap exhausted) — the caller then falls back
     * to `_dispatchWithRenumbering()`.
     */
    private _computeSortOrder(target: DropIndicator, draggedIssueUid: string): number | 'RENUMBER' {
        const draggedRowUid = this._rows.find(r => r.issue.uid === draggedIssueUid)?.row.uid ?? null;
        const insertBeforeRowUid = this._resolveInsertBeforeRowUid(target.insertBeforeRowUid, draggedRowUid);

        const neighbors = this._rows.filter(r => r.issue.uid !== draggedIssueUid).map(r => r.row);
        const insertBeforeIndex = insertBeforeRowUid === null
            ? neighbors.length
            : neighbors.findIndex(r => r.uid === insertBeforeRowUid);
        if (insertBeforeIndex === -1) {
            return 'RENUMBER'; // target row vanished from local state — safest fallback.
        }

        const prev = neighbors[insertBeforeIndex - 1];
        const next = neighbors[insertBeforeIndex];

        let candidate: number;
        if (!prev && !next) {
            candidate = 100;
        } else if (!prev) {
            candidate = next.sortOrder - 100;
        } else if (!next) {
            candidate = prev.sortOrder + 100;
        } else {
            candidate = Math.floor((prev.sortOrder + next.sortOrder) / 2);
        }

        if (candidate <= 0 || (prev && candidate <= prev.sortOrder) || (next && candidate >= next.sortOrder)) {
            return 'RENUMBER';
        }

        return candidate;
    }

    /**
     * A gap has `N+1` positions for `N` rows, so exactly TWO of them are
     * physically adjacent to the dragged row itself: the one right before it
     * (`insertBeforeRowUid === <its own row uid>`) and the one right after it
     * (which just names whatever row originally followed it). Both represent
     * "drop it back where it already was" — a genuine no-op, not a target
     * that "vanished" — but the first form can't be resolved by a plain
     * neighbor lookup, since the dragged row itself is excluded from
     * `neighbors` (that's the whole point of excluding it: a row is never
     * its own neighbor). This substitutes that self-referential uid with
     * whichever row ORIGINALLY followed the dragged one (or `null` if it was
     * last) — the neighbor lookup then resolves both adjacent gaps to the
     * exact same, correct (no-op) position.
     */
    private _resolveInsertBeforeRowUid(insertBeforeRowUid: string | null, draggedRowUid: string | null): string | null {
        if (insertBeforeRowUid === null || insertBeforeRowUid !== draggedRowUid) {
            return insertBeforeRowUid;
        }
        const draggedIndex = this._rows.findIndex(r => r.row.uid === draggedRowUid);
        return this._rows[draggedIndex + 1]?.row.uid ?? null;
    }

    /**
     * Collision fallback: renumber the ENTIRE visible column (0,100,200,...)
     * in the new order, as ONE `BulkCommand` (atomic — one transaction, one
     * audit-log entry) rather than N independent sequential dispatches.
     *
     * `labelChange` says whether this drop also moves the Issue INTO this column,
     * and it is what the caller must get right: the two cross-column call sites
     * pass `'move'` / `'assign'`, `_reorderWithinColumn()` passes nothing. Without
     * it this method only renumbered, and the label change — already applied to
     * `movedRow` in memory by the caller — was never sent (Stefan-Todo 0029-01):
     * the Issue rendered in its new column, then silently jumped back on the next
     * reload, because server-side it had never left the old one.
     *
     * Deliberately NOT unconditional: adding the command for a pure reorder would
     * rewrite `personalLabel` to the value it already holds — a pointless write and
     * a spurious audit-log row.
     */
    private async _dispatchWithRenumbering(
        issue: Issue,
        movedRow: IssuePersonalData,
        target: DropIndicator,
        labelChange: 'move' | 'assign' | null = null
    ): Promise<void> {
        const draggedRowUid = this._rows.find(r => r.issue.uid === issue.uid)?.row.uid ?? null;
        const insertBeforeRowUid = this._resolveInsertBeforeRowUid(target.insertBeforeRowUid, draggedRowUid);

        const others = this._rows.filter(r => r.issue.uid !== issue.uid).map(r => r.row);
        const foundIndex = insertBeforeRowUid === null ? -1 : others.findIndex(r => r.uid === insertBeforeRowUid);
        const insertBeforeIndex = foundIndex === -1 ? others.length : foundIndex;

        const newOrder = [...others];
        newOrder.splice(insertBeforeIndex, 0, movedRow);

        const changed: { row: IssuePersonalData; sortOrder: number }[] = [];
        newOrder.forEach((row, index) => {
            const sortOrder = (index + 1) * 100;
            if (row.sortOrder !== sortOrder) {
                changed.push({row, sortOrder});
            }
        });

        // Mutate BEFORE the split below, so the Move/Assign command — whose payload
        // is this very row object — carries the final sortOrder.
        changed.forEach(({row, sortOrder}) => {
            row.sortOrder = sortOrder;
        });

        // The moved row is carried by its own Move/Assign command, so it must not
        // ALSO get a Reorder: that would write the same row twice in one transaction,
        // and on an Assign the row is brand new from `repository.create()`, carrying a
        // uid the backend has never seen — its Reorder would fail with "No
        // IssuePersonalData with UID" and take the whole atomic save down with it.
        const reordered = labelChange === null
            ? changed
            : changed.filter(({row}) => row.uid !== movedRow.uid);

        const commands: ICommand<IssuePersonalData>[] = [];
        if (labelChange === 'move') {
            commands.push(new MoveIssueToAnotherPersonalLabelCommand('issue-personal-data', movedRow.uid, movedRow));
        } else if (labelChange === 'assign') {
            commands.push(new AssignIssueToPersonalLabelCommand('issue-personal-data', movedRow.uid, movedRow));
        }
        // The label change goes FIRST: on an Assign it is what makes the row exist
        // server-side, and the renumbering behind it may reference it.
        commands.push(...reordered.map(({row}) =>
            new ReorderIssueOfPersonalLabelCommand('issue-personal-data', row.uid, row)
        ));

        const commandBus = container.resolve(CommandBus);
        const dispatched = commands.length === 0 || await commandBus.dispatchCommand(new BulkCommand(commands));

        if (dispatched) {
            this._rows = newOrder.map(row => {
                const existing = this._rows.find(r => r.row.uid === row.uid);
                return existing ? {issue: existing.issue, row} : {issue, row};
            });
            this._justMoved.flash(issue.uid);
            // Mirrors the non-collision Move/Assign paths above. Before this, the
            // early return into this method swallowed the event, so a drop that
            // happened to hit an exhausted gap left the other columns stale.
            if (labelChange !== null) {
                window.dispatchEvent(new CustomEvent<MyOrganizerIssueLabeledEventDetail>(
                    MY_ORGANIZER_ISSUE_LABELED_EVENT, {detail: {issueUid: issue.uid, personalLabelUid: this.label.uid}}
                ));
            }
        } else {
            console.error('[my-organizer-label-column] failed to renumber the column after a collision');
            await this._load();
        }
    }

}
